import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import FilterPopover from "../components/FilterPopover";
import DuplicateWarningModal from "../components/DuplicateWarningModal";
import { checkDuplicates } from "../lib/dedupe";
import PragmaSelect from "../components/PragmaSelect";
import { useAuth } from "../context/AuthContext";
import CompanyAutocomplete from "../components/CompanyAutocomplete";

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [moves, setMoves] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState({ owner: [], company: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [c, u, m] = await Promise.all([
        api.get("/contacts"),
        api.get("/users"),
        api.get("/moves"),
      ]);
      setContacts(c.data); setUsers(u.data); setMoves(m.data);
    } catch (e) { toast.error("Failed to load contacts"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const lastActivity = (cid) => {
    const m = moves
      .filter((m) => m.linked_type === "contact" && m.linked_id === cid && m.status === "done")
      .sort((a, b) => (a.completed_at < b.completed_at ? 1 : -1))[0];
    return m?.completed_at?.slice(0, 10) || "—";
  };

  const filtered = useMemo(() => {
    let rows = contacts;
    if (filterState.owner.length > 0) {
      const ids = new Set(filterState.owner);
      rows = rows.filter((c) => ids.has(c.owner_id));
    }
    if (filterState.company.length > 0) {
      const set = new Set(filterState.company);
      rows = rows.filter((c) => set.has(c.company || "—"));
    }
    return rows;
  }, [contacts, filterState]);

  const companyOptions = useMemo(() => {
    const names = new Set();
    contacts.forEach((c) => { if (c.company) names.add(c.company); });
    return Array.from(names).sort().map((n) => ({ value: n, label: n }));
  }, [contacts]);

  return (
    <div data-testid="contacts-page">
      <PageHeader title="Contacts" subtitle={`${filtered.length} records`}>
        <button className="pg-btn pg-btn-primary" onClick={() => setShowForm(true)} data-testid="new-contact-btn">
          <Plus size={14} /> New Contact
        </button>
      </PageHeader>

      <div className="px-8 py-4 flex items-center gap-2" style={{ borderBottom: "1px solid var(--pg-border)" }}>
        <FilterPopover
          testid="contacts-filter"
          matchCount={filtered.length}
          value={filterState}
          onChange={setFilterState}
          groups={[
            {
              key: "owner",
              title: "Owner",
              options: users.filter((u) => u.active !== false).map((u) => ({ value: u.id, label: u.name })),
            },
            { key: "company", title: "Company", options: companyOptions },
          ]}
        />
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="px-8 py-20 text-center text-mute mono text-xs">loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-8 py-12">
            <div className="empty-state">
              <div className="text-lg mb-2" style={{ color: "var(--pg-text)" }}>No contacts yet</div>
              <div className="mono text-xs">Convert a lead, or create one manually.</div>
            </div>
          </div>
        ) : (
          <table className="pg-table">
            <thead>
              <tr>
                <th>Name</th><th>Company</th><th>Email</th><th>Phone</th>
                <th>Owner</th><th>Last Activity</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/contacts/${c.id}`)} data-testid={`contact-row-${c.id}`}>
                  <td>{c.name}</td>
                  <td className="text-dim">{c.company || "—"}</td>
                  <td className="text-dim text-sm">{c.email || "—"}</td>
                  <td className="text-dim text-sm">{c.phone || "—"}</td>
                  <td className="text-dim text-sm">{userName(c.owner_id)}</td>
                  <td className="mono text-xs text-dim">{lastActivity(c.id)}</td>
                  <td className="mono text-xs text-dim">{(c.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && <NewContactForm users={users} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function NewContactForm({ users, onClose, onCreated }) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", role: "", address: "", notes: "",
    owner_id: currentUser?.id || users[0]?.id, company_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [dedupeMatches, setDedupeMatches] = useState(null); // { matches } when triggered

  const doCreate = async () => {
    setSaving(true);
    try { await api.post("/contacts", form); toast.success("Contact created"); onCreated(); }
    catch { toast.error("Failed"); }
    setSaving(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { hasMatches, matches } = await checkDuplicates({
      entity_type: "contact",
      email: form.email, phone: form.phone, company: form.company,
    });
    setSaving(false);
    if (hasMatches) { setDedupeMatches(matches); return; }
    doCreate();
  };

  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} />
      <div className="side-panel" data-variant="center" data-testid="new-contact-panel">
        <div className="px-6 py-5" style={{ borderBottom: "1px solid var(--pg-border)" }}>
          <h2 className="text-xl">New Contact</h2>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {[["name", "Name", true], ["email", "Email"], ["phone", "Phone"], ["role", "Role"], ["address", "Address"]].map(([k, label, req]) => (
            <div key={k}>
              <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">{label}</label>
              <input className="pg-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={req} data-testid={`contact-input-${k}`} />
            </div>
          ))}
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Company</label>
            <CompanyAutocomplete
              value={form.company}
              companyId={form.company_id}
              onChange={({ name, company_id }) => setForm({ ...form, company: name, company_id })}
              inputTestId="contact-input-company"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Owner</label>
            <PragmaSelect
              value={form.owner_id}
              onChange={(v) => setForm({ ...form, owner_id: v })}
              options={users.map((u) => ({ value: u.id, label: u.name, hint: u.job_title || u.email }))}
              testId="contact-input-owner"
            />
          </div>
          <div>
            <label className="block mono text-[11px] text-mute mb-1 uppercase tracking-wider">Notes</label>
            <textarea className="pg-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </form>
        <div className="px-6 py-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--pg-border)" }}>
          <button type="button" className="pg-btn" onClick={onClose}>Cancel</button>
          <button onClick={submit} className="pg-btn pg-btn-primary" disabled={saving} data-testid="contact-submit">{saving ? "Saving…" : "Create"}</button>
        </div>
      </div>
      {dedupeMatches && (
        <DuplicateWarningModal
          matches={dedupeMatches}
          entityLabel="Contact"
          onCancel={() => setDedupeMatches(null)}
          onContinue={() => { setDedupeMatches(null); doCreate(); }}
          onOpenExisting={(row, kind) => {
            setDedupeMatches(null);
            onClose();
            navigate(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`);
          }}
          onMergeInto={async (row, kind) => {
            // Fill in any new info the user typed that the target is missing.
            const fields = ["email", "phone", "company", "role", "address", "notes"];
            const patch = {};
            for (const f of fields) {
              if ((form[f] || "").trim() && !(row[f] || "").trim()) patch[f] = form[f];
            }
            if (form.company_id && !row.company_id) patch.company_id = form.company_id;
            try {
              if (Object.keys(patch).length) {
                await api.patch(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`, patch);
                toast.success(`Merged your inputs into ${row.name}`);
              } else {
                toast.info(`No new info to merge into ${row.name}`);
              }
              setDedupeMatches(null);
              onClose();
              navigate(kind === "leads" ? `/leads/${row.id}` : `/contacts/${row.id}`);
            } catch (e) {
              toast.error("Merge failed — please try again");
            }
          }}
        />
      )}
    </>
  );
}
